import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Request Builders for Accounting Invoice Operations
 *
 * These builders replace the legacy accounting-invoice adapter methods.
 * All state-changing operations should be followed by model.reload() in the caller.
 */

// ============================================================================
// State Change Operations
// ============================================================================

/**
 * Approve an accounting invoice (CREATED -> APPROVED)
 */
export function approveInvoiceRequest(id: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/approve`,
    method: 'PUT',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Unlock an accounting invoice (APPROVED -> CREATED)
 */
export function unlockInvoiceRequest(id: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/unlock`,
    method: 'PUT',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Fund an accounting invoice (APPROVED -> FUNDED)
 */
export function fundInvoiceRequest(id: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/fund`,
    method: 'PUT',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Void an accounting invoice (CREATED or APPROVED -> VOIDED)
 */
export function voidInvoiceRequest(id: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/void`,
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Update line items for an accounting invoice
 */
export function updateLineItemsRequest(id: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/update_line_items`,
    method: 'PUT',
    cacheOptions: { [SkipCache]: true },
  };
}

// ============================================================================
// Creation Operation
// ============================================================================

/**
 * Create a new accounting invoice for a company
 */
export function createInvoiceRequest(companyId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/create_invoice`,
    method: 'POST',
    body: JSON.stringify({ company_id: companyId }),
    cacheOptions: { [SkipCache]: true },
  };
}

// ============================================================================
// Download Operations
// ============================================================================

export interface PdfOptions {
  dueDate: string;
  poNumber?: string;
  instructions?: string;
}

/**
 * Download invoice as CSV
 */
export function downloadInvoiceCsvRequest(id: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/download_csv`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Generate and download invoice as PDF with custom options
 */
export function downloadInvoicePdfRequest(id: string, options: PdfOptions): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounting-invoices' },
    op: 'query',
    resourcePath: 'accounting-invoices',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/${id}/download_pdf`,
    method: 'POST',
    body: JSON.stringify({
      due_date: options.dueDate,
      po_number: options.poNumber,
      instructions: options.instructions,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Download multiple invoices as a bulk CSV/ZIP
 *
 * NOTE: This endpoint returns binary data (ZIP files).
 * Use the adapter method `downloadCsvBulk` instead for proper binary handling.
 * Request Manager doesn't handle binary responses well for downloads.
 */
